/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/**
 * Property video rules shared by the drawer and the detail page.
 *
 * Mirrors the backend: links (YouTube, Vimeo) live in `media.videos`, files
 * are uploaded to the property's `videos/` directory, and both share one
 * limit. The size bounds must stay in step with `property_video_service.py`,
 * because the presigned POST is the only server-side gate on the object size.
 */

/** Links and uploaded files together. */
export const MAX_PROPERTY_VIDEOS = 5;

export const MAX_VIDEO_UPLOAD_BYTES = 1024 * 1024 * 1024;
export const MIN_VIDEO_UPLOAD_BYTES = 1024;

export const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/quicktime', 'video/webm'];

export interface VideoEmbed {
  provider: 'youtube' | 'vimeo';
  /** The provider's own player URL, safe to put in an iframe. */
  embedUrl: string;
}

const YOUTUBE_HOSTS = new Set(['youtube.com', 'www.youtube.com', 'm.youtube.com']);
const VIMEO_HOSTS = new Set(['vimeo.com', 'www.vimeo.com', 'player.vimeo.com']);
const YOUTUBE_ID = /^[\w-]{6,}$/;
const YOUTUBE_PATH_PREFIXES = ['/shorts/', '/embed/', '/live/'];

/**
 * Returns the video id from a youtu.be short link, or null when the path is empty.
 */
function youtuBeId(url: URL): string | null {
  return url.pathname.slice(1).split('/')[0] || null;
}

/**
 * Returns the video id of a YouTube watch, short, live or embed page.
 */
function youtubeId(url: URL): string | null {
  if (url.hostname === 'youtu.be') return youtuBeId(url);
  if (!YOUTUBE_HOSTS.has(url.hostname)) return null;
  if (url.pathname === '/watch') return url.searchParams.get('v');
  const prefix = YOUTUBE_PATH_PREFIXES.find((p) => url.pathname.startsWith(p));
  return prefix ? url.pathname.slice(prefix.length).split('/')[0] : null;
}

/**
 * Returns the numeric id of a Vimeo page or player URL.
 */
function vimeoId(url: URL): string | null {
  if (!VIMEO_HOSTS.has(url.hostname)) return null;
  const match = url.pathname.match(/^\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
}

/**
 * Parses an absolute http(s) URL, or returns null.
 */
function parseHttpUrl(raw: string): URL | null {
  try {
    const url = new URL(raw.trim());
    return url.protocol === 'http:' || url.protocol === 'https:' ? url : null;
  } catch {
    return null;
  }
}

/**
 * Turns a YouTube or Vimeo link into its embeddable player, or null when the
 * link is not a video page on either provider.
 */
export function parseVideoLink(raw: string): VideoEmbed | null {
  const url = parseHttpUrl(raw);
  if (!url) return null;
  const yt = youtubeId(url);
  if (yt && YOUTUBE_ID.test(yt)) {
    return { provider: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${yt}` };
  }
  const vimeo = vimeoId(url);
  return vimeo ? { provider: 'vimeo', embedUrl: `https://player.vimeo.com/video/${vimeo}` } : null;
}

/**
 * Why a picked file cannot be uploaded, or null when it can.
 */
export function videoFileProblem(file: File): 'type' | 'tooLarge' | 'tooSmall' | null {
  if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) return 'type';
  if (file.size > MAX_VIDEO_UPLOAD_BYTES) return 'tooLarge';
  if (file.size < MIN_VIDEO_UPLOAD_BYTES) return 'tooSmall';
  return null;
}
